import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface WeightCalculatorCell {
  wtPct: number | null;
  grams: number | null;
}

export interface WeightCalculatorRow {
  name: string;
  p1: WeightCalculatorCell;
  p2: WeightCalculatorCell;
}

export interface WeightCalculatorPackage {
  name: string;
  treat1: number | null;
  treat2: number | null;
}

/** Raw Finished Oil selections; source of truth for treat wt% used to derive rows. */
export interface WeightCalculatorFinishedOilSource {
  baseOilData?: any[];
  vmSelectedData?: any[];
  ppdData?: any[];
  packages?: WeightCalculatorPackage[];
}

export interface WeightCalculatorContext {
  finishedOil?: WeightCalculatorFinishedOilSource;
  blend1Weight?: string | number;
  blend2Weight?: string | number;
}

@Component({
  selector: 'app-bc-weight-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, A11yModule],
  templateUrl: './bc-weight-calculator.component.html',
  styleUrl: './bc-weight-calculator.component.scss'
})
export class BcWeightCalculatorComponent {
  readonly defaultWeight = 100;

  @Input() isOpen = false;
  @Input() context: WeightCalculatorContext = {};
  @Output() closed = new EventEmitter<void>();

  get rows(): WeightCalculatorRow[] {
    const source = this.context?.finishedOil ?? {};
    const weight1 = this.isValidWeight(this.blend1Weight) ? this.toPositiveNumber(this.blend1Weight) : null;
    const weight2 = this.isValidWeight(this.blend2Weight) ? this.toPositiveNumber(this.blend2Weight) : null;

    return [
      ...this.buildPackageRows(source.packages, weight1, weight2),
      ...this.buildNamedRows(source.vmSelectedData, item => item?.selectedValue, weight1, weight2),
      ...this.buildNamedRows(source.ppdData, item => item?.ppd ?? item?.selectedValue ?? item?.name, weight1, weight2),
      ...this.buildNamedRows(source.baseOilData, item => item?.oil ?? item?.name, weight1, weight2),
    ];
  }

  get totals(): { p1: WeightCalculatorCell; p2: WeightCalculatorCell } {
    const rows = this.rows;
    const sum = (pick: (row: WeightCalculatorRow) => number | null): number | null => {
      const values = rows.map(pick).filter((value): value is number => value !== null);
      return values.length ? this.round2(values.reduce((acc, value) => acc + value, 0)) : null;
    };
    return {
      p1: { wtPct: sum(row => row.p1.wtPct), grams: sum(row => row.p1.grams) },
      p2: { wtPct: sum(row => row.p2.wtPct), grams: sum(row => row.p2.grams) },
    };
  }

  get blend1Weight(): string | number {
    return this.context?.blend1Weight === undefined ? this.defaultWeight : this.context.blend1Weight;
  }

  set blend1Weight(value: string | number) {
    this.context = { ...this.context, blend1Weight: value };
  }

  get blend2Weight(): string | number {
    return this.context?.blend2Weight === undefined ? this.defaultWeight : this.context.blend2Weight;
  }

  set blend2Weight(value: string | number) {
    this.context = { ...this.context, blend2Weight: value };
  }

  isValidWeight(value: string | number): boolean {
    return /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(String(value)) && Number(value) > 0;
  }

  get areWeightsValid(): boolean {
    return this.isValidWeight(this.blend1Weight) && this.isValidWeight(this.blend2Weight);
  }

  get canPrint(): boolean {
    return this.areWeightsValid && this.rows.some(row =>
      [row.p1.wtPct, row.p2.wtPct].some(value => value !== null)
    );
  }

  weightError(value: string | number): string {
    if (value === '' || value === null || value === undefined) {
      return 'Enter a blend weight.';
    }

    if (!this.isValidWeight(value)) {
      return 'Enter a positive weight with no more than 2 decimal places.';
    }

    return '';
  }

  close(): void {
    this.closed.emit();
  }

  private buildNamedRows(
    items: any[] | undefined,
    getName: (item: any) => string | undefined,
    weight1: number | null,
    weight2: number | null,
  ): WeightCalculatorRow[] {
    if (!items?.length) {
      return [];
    }

    const rows: WeightCalculatorRow[] = [];
    items.forEach(item => {
      const name = getName(item);
      if (!name) {
        return;
      }
      const p1 = this.buildCell(item?.treat1, weight1);
      const p2 = this.buildCell(item?.treat2, weight2);
      if (p1.wtPct === null && p2.wtPct === null) {
        return;
      }
      rows.push({ name, p1, p2 });
    });
    return rows;
  }

  private buildPackageRows(
    packages: WeightCalculatorPackage[] | undefined,
    weight1: number | null,
    weight2: number | null,
  ): WeightCalculatorRow[] {
    if (!packages?.length) {
      return [];
    }

    const rows: WeightCalculatorRow[] = [];
    packages.forEach(pkg => {
      if (!pkg?.name) {
        return;
      }
      const p1 = this.buildCell(pkg.treat1, weight1);
      const p2 = this.buildCell(pkg.treat2, weight2);
      if (p1.wtPct === null && p2.wtPct === null) {
        return;
      }
      rows.push({ name: pkg.name, p1, p2 });
    });
    return rows;
  }

  private buildCell(rawWtPct: unknown, weight: number | null): WeightCalculatorCell {
    const wtPct = this.toPositiveNumber(rawWtPct);
    if (wtPct === null) {
      return { wtPct: null, grams: null };
    }
    const grams = weight !== null ? this.round2((wtPct / 100) * weight) : null;
    return { wtPct: this.round2(wtPct), grams };
  }

  private toPositiveNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string' && !/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  print(): void {
    if (this.canPrint) {
      window.print();
    }
  }
}
